import React from 'react';
import { motion } from 'framer-motion';

const Card = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1], delay }}
    className="w-full rounded-3xl border border-gray-200 bg-white shadow-sm p-8 lg:p-10"
  >
    {children}
  </motion.div>
);

const SecurityPolicy = () => {
  return (
    <main className="min-h-screen bg-white text-gray-900">
      {/* Prominent Hero: wave grid with mesh accents */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 pointer-events-none">
          {/* Single mesh graphic (similar to Privacy policy, but varied colors/weights) */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.55 }} transition={{ duration: 1 }} className="absolute -top-24 left-1/2 -translate-x-1/2 w-[880px] h-[880px]">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 36, repeat: Infinity, ease: 'linear' }} className="w-full h-full">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                {/* Outer nodes */}
                <circle cx="20" cy="20" r="1.6" fill="#2563eb" opacity="0.85" />
                <circle cx="80" cy="20" r="1.6" fill="#14b8a6" opacity="0.85" />
                <circle cx="20" cy="80" r="1.6" fill="#f59e0b" opacity="0.85" />
                <circle cx="80" cy="80" r="1.6" fill="#ef4444" opacity="0.85" />

                {/* Middle nodes */}
                <circle cx="35" cy="35" r="1.2" fill="#2563eb" opacity="0.8" />
                <circle cx="65" cy="35" r="1.2" fill="#14b8a6" opacity="0.8" />
                <circle cx="35" cy="65" r="1.2" fill="#f59e0b" opacity="0.8" />
                <circle cx="65" cy="65" r="1.2" fill="#ef4444" opacity="0.8" />

                {/* Outer frame connections */}
                <line x1="20" y1="20" x2="80" y2="20" stroke="#60a5fa" strokeWidth="0.28" opacity="0.4">
                  <animate attributeName="opacity" values="0.2;0.6;0.2" dur="2s" repeatCount="indefinite" />
                </line>
                <line x1="20" y1="80" x2="80" y2="80" stroke="#fbbf24" strokeWidth="0.28" opacity="0.4">
                  <animate attributeName="opacity" values="0.2;0.6;0.2" dur="2.2s" repeatCount="indefinite" />
                </line>
                <line x1="20" y1="20" x2="20" y2="80" stroke="#a78bfa" strokeWidth="0.28" opacity="0.35">
                  <animate attributeName="opacity" values="0.2;0.55;0.2" dur="1.8s" repeatCount="indefinite" />
                </line>
                <line x1="80" y1="20" x2="80" y2="80" stroke="#34d399" strokeWidth="0.28" opacity="0.35">
                  <animate attributeName="opacity" values="0.2;0.55;0.2" dur="2.4s" repeatCount="indefinite" />
                </line>

                {/* Inner connections */}
                <line x1="35" y1="35" x2="65" y2="35" stroke="#60a5fa" strokeWidth="0.22" opacity="0.45">
                  <animate attributeName="opacity" values="0.2;0.5;0.2" dur="1.6s" repeatCount="indefinite" />
                </line>
                <line x1="35" y1="65" x2="65" y2="65" stroke="#f87171" strokeWidth="0.22" opacity="0.45">
                  <animate attributeName="opacity" values="0.2;0.5;0.2" dur="1.9s" repeatCount="indefinite" />
                </line>
                <line x1="35" y1="35" x2="35" y2="65" stroke="#34d399" strokeWidth="0.22" opacity="0.4">
                  <animate attributeName="opacity" values="0.15;0.45;0.15" dur="1.7s" repeatCount="indefinite" />
                </line>
                <line x1="65" y1="35" x2="65" y2="65" stroke="#f59e0b" strokeWidth="0.22" opacity="0.4">
                  <animate attributeName="opacity" values="0.15;0.45;0.15" dur="2.1s" repeatCount="indefinite" />
                </line>

                {/* Cross connections with lighter weight for differentiated look */}
                <line x1="20" y1="20" x2="65" y2="65" stroke="#93c5fd" strokeWidth="0.14" opacity="0.3">
                  <animate attributeName="opacity" values="0.08;0.28;0.08" dur="3.2s" repeatCount="indefinite" />
                </line>
                <line x1="80" y1="20" x2="35" y2="65" stroke="#6ee7b7" strokeWidth="0.14" opacity="0.3">
                  <animate attributeName="opacity" values="0.08;0.28;0.08" dur="3.0s" repeatCount="indefinite" />
                </line>
                <line x1="20" y1="80" x2="65" y2="35" stroke="#fde68a" strokeWidth="0.14" opacity="0.3">
                  <animate attributeName="opacity" values="0.08;0.28;0.08" dur="3.4s" repeatCount="indefinite" />
                </line>
                <line x1="80" y1="80" x2="35" y2="35" stroke="#fca5a5" strokeWidth="0.14" opacity="0.3">
                  <animate attributeName="opacity" values="0.08;0.28;0.08" dur="2.8s" repeatCount="indefinite" />
                </line>
              </svg>
            </motion.div>
          </motion.div>
        </div>

        <div className="container-custom py-24 lg:py-32 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-6xl font-bold font-slim tracking-tight">Security Policy — FlowMatic</h1>
            <p className="mt-4 text-lg text-gray-600">Last Updated: October 2025</p>
          </motion.div>
        </div>
      </section>

      <section className="container-custom py-16 lg:py-20">
        <div className="max-w-5xl mx-auto space-y-8">
          <Card>
            <p className="text-lg leading-relaxed text-gray-700">
              At FlowMatic, we understand that trust and data security are at the heart of automation. We are committed to maintaining the confidentiality, integrity, and availability of all data processed through our AI systems and automation tools.
            </p>
          </Card>

          <Card delay={0.05}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Our Commitment to Security</span>
            </h2>
            <p className="text-gray-700">We apply enterprise-grade security standards across all operations — from AI development to system deployment. Our goal is to ensure that your business data, communications, and automations are protected at every stage of interaction with our platform.</p>
          </Card>

          <Card delay={0.1}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Data Protection</span>
            </h2>
            <p className="text-gray-700 mb-2">We take data protection seriously. All client data is:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Encrypted during transmission (using SSL/TLS protocols).</li>
              <li>Stored securely in protected environments with limited access.</li>
              <li>Never shared with third parties without explicit client consent.</li>
            </ul>
            <p className="text-gray-700 mt-3">We follow a data minimization principle — collecting and storing only what’s necessary to deliver and maintain your automation systems.</p>
          </Card>

          <Card delay={0.15}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Access Control</span>
            </h2>
            <p className="text-gray-700 mb-2">Access to client projects and systems is strictly restricted to authorized personnel only. Our access management includes:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Role-based permissions for team members.</li>
              <li>Mandatory authentication for all internal tools.</li>
              <li>Session monitoring and access logging for accountability.</li>
            </ul>
            <p className="text-gray-700 mt-3">We ensure that only the right people can access your data — and only when absolutely necessary.</p>
          </Card>

          <Card delay={0.2}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Secure Development Practices</span>
            </h2>
            <p className="text-gray-700 mb-2">Every AI agent and automation workflow we build goes through:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Code reviews for quality and safety.</li>
              <li>Continuous vulnerability scanning.</li>
              <li>Regular dependency updates and patch management.</li>
            </ul>
            <p className="text-gray-700 mt-3">We follow the “security by design” principle — meaning safety and reliability are built into every stage of development, not added later.</p>
          </Card>

          <Card delay={0.25}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Infrastructure Security</span>
            </h2>
            <p className="text-gray-700 mb-2">Our infrastructure is hosted on trusted cloud platforms that comply with international security standards (such as ISO 27001, SOC 2, and GDPR where applicable). We maintain:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Firewalls and intrusion detection systems.</li>
              <li>Continuous uptime monitoring.</li>
              <li>Regular security backups and disaster recovery protocols.</li>
            </ul>
          </Card>

          <Card delay={0.3}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Client Responsibility</span>
            </h2>
            <p className="text-gray-700 mb-2">While we provide strong infrastructure and system security, clients also play a role in maintaining overall protection. We recommend that clients:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Use strong and unique passwords for all connected services.</li>
              <li>Limit access to authorized personnel only.</li>
              <li>Keep API keys and credentials secure.</li>
            </ul>
            <p className="text-gray-700 mt-3">FlowMatic is not responsible for security breaches caused by client-side negligence or insecure third-party integrations.</p>
          </Card>

          <Card delay={0.35}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Incident Response</span>
            </h2>
            <p className="text-gray-700 mb-2">In the rare event of a security incident, we take immediate action to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Identify and contain the threat.</li>
              <li>Notify affected clients promptly.</li>
              <li>Restore services securely.</li>
              <li>Conduct a full root-cause analysis to prevent recurrence.</li>
            </ul>
          </Card>

          <Card delay={0.4}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Compliance and Monitoring</span>
            </h2>
            <p className="text-gray-700">FlowMatic complies with global data protection standards, ensuring secure processing, transparency, and accountability. We regularly audit internal processes to maintain compliance and strengthen system security.</p>
          </Card>

          <Card delay={0.45}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Contact Us</span>
            </h2>
            <p className="text-gray-700">If you believe your data has been compromised or have a security concern, please contact us immediately:</p>
            <p className="mt-2 font-medium">security@flowmatic.com</p>
            <a href="https://www.flowmatic.com" target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-blue-600 hover:text-purple-600 transition-colors">www.flowmatic.com</a>
          </Card>
        </div>
      </section>
    </main>
  );
};

export default SecurityPolicy;


